/* Hush — RevenueCat / IAP wrapper.
   Web: no-op (returns mock free tier).
   Native: real RevenueCat SDK.
   Configure in /app/frontend/.env:
     REACT_APP_REVENUECAT_IOS_KEY    (appl_xxx)
     REACT_APP_REVENUECAT_ANDROID_KEY (goog_xxx)
*/
import { isNative, platform } from "@/lib/platform";
import { api } from "@/lib/api";

let Purchases = null;
try {
  // eslint-disable-next-line global-require
  Purchases = require("@revenuecat/purchases-capacitor").Purchases;
} catch (err) {
  console.warn("[iap] RevenueCat SDK not available (non-fatal):", err);
}

const RC_IOS_KEY = process.env.REACT_APP_REVENUECAT_IOS_KEY || "";
const RC_ANDROID_KEY = process.env.REACT_APP_REVENUECAT_ANDROID_KEY || "";
const ENTITLEMENT_PLUS = "plus";

let _configured = false;
let _lastError = null;
let _lastOfferingsRaw = null;

// Best-effort backend telemetry so we can debug IAP failures on devices
// without Safari Web Inspector. Silently no-ops if /api/errors isn't reachable.
async function _logToBackend(stage, payload) {
  try {
    await api.post("/errors", {
      message: `[iap:${stage}] ${typeof payload === "string" ? payload : JSON.stringify(payload).slice(0, 800)}`,
      ua: typeof navigator !== "undefined" ? navigator.userAgent : "",
      url: typeof window !== "undefined" ? window.location.href : "",
      platform: platform(),
    });
  } catch (_) { /* ignore — telemetry is best-effort */ }
}

export async function initIAP(userId) {
  if (!isNative() || !Purchases) return false;
  const apiKey = platform() === "ios" ? RC_IOS_KEY : RC_ANDROID_KEY;
  if (!apiKey) {
    _lastError = "missing api key for platform " + platform();
    console.warn("[iap]", _lastError);
    _logToBackend("init", _lastError);
    return false;
  }
  if (_configured) return true;
  try {
    await Purchases.configure({ apiKey, appUserID: userId });
    _configured = true;
    console.log("[iap] configured ok, platform=", platform(), "user=", userId);
    _logToBackend("init", { ok: true, platform: platform(), user: userId });
    return true;
  } catch (e) {
    _lastError = String(e?.message || e);
    console.error("[iap] configure failed:", e);
    _logToBackend("init", { ok: false, error: _lastError });
    return false;
  }
}

export async function getOfferings() {
  if (!_configured) {
    console.warn("[iap] getOfferings called before configure");
    return null;
  }
  try {
    const res = await Purchases.getOfferings();
    _lastOfferingsRaw = res;
    // Detailed log — what does RC actually see?
    const summary = {
      hasCurrent: Boolean(res?.current),
      currentId: res?.current?.identifier || null,
      packageCount: res?.current?.availablePackages?.length || 0,
      packageIds: (res?.current?.availablePackages || []).map((p) => p?.identifier),
      productIds: (res?.current?.availablePackages || []).map((p) => p?.product?.identifier),
      allOfferingIds: Object.keys(res?.all || {}),
    };
    console.log("[iap] getOfferings →", summary);
    _logToBackend("getOfferings", summary);
    return res?.current || null;
  } catch (e) {
    _lastError = String(e?.message || e);
    console.error("[iap] getOfferings failed:", e);
    _logToBackend("getOfferings", { ok: false, error: _lastError, code: e?.code, underlyingError: e?.underlyingErrorMessage });
    return null;
  }
}

export function getLastIAPError() {
  return _lastError;
}

export async function purchasePackage(rcPackage) {
  if (!_configured) throw new Error("Purchases not ready");
  try {
    const res = await Purchases.purchasePackage({ aPackage: rcPackage });
    const ent = res?.customerInfo?.entitlements?.active?.[ENTITLEMENT_PLUS];
    _logToBackend("purchase", { ok: true, entitled: Boolean(ent), pkg: rcPackage?.identifier });
    return Boolean(ent);
  } catch (e) {
    _lastError = String(e?.message || e);
    console.error("[iap] purchase failed:", e);
    _logToBackend("purchase", { ok: false, error: _lastError, code: e?.code, userCancelled: e?.userCancelled });
    throw e;
  }
}

export async function restorePurchases() {
  if (!_configured) return false;
  try {
    const res = await Purchases.restorePurchases();
    const ent = res?.customerInfo?.entitlements?.active?.[ENTITLEMENT_PLUS];
    return Boolean(ent);
  } catch (e) {
    _lastError = String(e?.message || e);
    console.error("[iap] restore failed:", e);
    _logToBackend("restore", { ok: false, error: _lastError });
    return false;
  }
}

export async function hasActiveSubscription() {
  if (!_configured) return false;
  try {
    const res = await Purchases.getCustomerInfo();
    return Boolean(res?.customerInfo?.entitlements?.active?.[ENTITLEMENT_PLUS]);
  } catch {
    return false;
  }
}
